// Utilities to build WhatsApp deep-links from an order + status change.
// No 3rd-party SDK: opens WhatsApp Web/App via https://wa.me/{phone}?text={msg}

const currency = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

// Best-effort E.164 formatting for MX numbers if country code missing.
export function normalizePhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d]/g, "");
  if (!digits) return "";
  // If it already looks international (12–15 digits and starts with 1-9), keep as is.
  if (digits.length >= 11) return digits;
  // 10-digit Mexican mobile → prepend 52 country code.
  if (digits.length === 10) return `52${digits}`;
  return digits;
}

const STATUS_TEMPLATE = {
  pendiente: (o, brand) =>
    `Hola ${o._name}, recibimos tu pedido *${o._id}* en ${brand}. ` +
    `Total: ${currency(o.total)}. ` +
    (o.payment_method === "transferencia"
      ? `Para iniciar tu producción necesitamos el anticipo del 50%: *${currency(o.deposit_amount || o.total * 0.5)}*. ` +
        `Te enviamos los datos bancarios por este medio. Cuando hagas la transferencia, mándanos el comprobante 🙌`
      : o.payment_method === "efectivo"
        ? `Pago en efectivo — coordinamos contigo la entrega/recolección.`
        : `Pago por tarjeta recibido. Ya estamos preparando tu pedido ✨`),

  en_produccion: (o, brand) =>
    `Hola ${o._name}, tu pedido *${o._id}* en ${brand} ya está *en producción* 🖨️. ` +
    `Tiempo estimado: ${o.delivery_estimate || "algunos días"}. ` +
    `Te aviso en cuanto esté listo.`,

  listo: (o, brand) =>
    `Hola ${o._name}, ¡tu pedido *${o._id}* está *listo*! 🎉 ` +
    (o.delivery_method === "sucursal"
      ? `Puedes pasar a recogerlo cuando gustes${o._pickup ? ` en ${o._pickup}` : ""}. ` +
        (o.payment_method === "efectivo" || !o.final_paid
          ? `Recuerda traer el finiquito: *${currency((o.total || 0) - (o.deposit_amount || o.total * 0.5))}*.`
          : "")
      : `Programamos el envío a tu domicilio. ` +
        (o.payment_method === "efectivo" || !o.final_paid
          ? `Ten listo el finiquito: *${currency((o.total || 0) - (o.deposit_amount || o.total * 0.5))}*.`
          : "")),

  enviado: (o, brand) =>
    `Hola ${o._name}, tu pedido *${o._id}* de ${brand} ya fue *enviado* 📦. ` +
    `Llega a: ${o._address || "la dirección registrada"}. ` +
    `Cualquier duda por aquí estamos.`,

  entregado: (o, brand) =>
    `Hola ${o._name}, confirmamos la *entrega* de tu pedido *${o._id}*. ` +
    `¡Gracias por confiar en ${brand}! 🙌 Nos encantaría verte etiquetarnos si compartes tu producto en redes.`,

  cancelado: (o, brand) =>
    `Hola ${o._name}, tu pedido *${o._id}* en ${brand} fue *cancelado*. ` +
    `Si fue un error o quieres retomarlo, escríbenos por este medio.`,

  // Legacy fallbacks
  confirmado: (o, brand) =>
    `Hola ${o._name}, tu pedido *${o._id}* en ${brand} está confirmado.`,
  en_diseño: (o, brand) =>
    `Hola ${o._name}, tu pedido *${o._id}* en ${brand} está en la fase de diseño.`,

  // Manual outreach templates (not tied to a status change)
  follow_up: (o, brand) =>
    `Hola ${o._name}, te escribo de *${brand}* para dar seguimiento a tu pedido *${o._id}*. ` +
    `¿Tienes alguna duda o comentario sobre la propuesta? Estamos para ayudarte a que quede perfecto ✨`,

  review: (o, brand) =>
    `Hola ${o._name}, esperamos que tu pedido *${o._id}* de *${brand}* haya cumplido tus expectativas 🙌 ` +
    `Si te fue útil, nos ayudarías muchísimo dejándonos una reseña en Google: ${o._review_url}`,
};

export function buildStatusMessage(order, settings, statusOverride) {
  if (!order) return "";
  const brand = settings?.brand_name || "Printlab";
  const status = statusOverride || order.status || "pendiente";
  const enriched = {
    ...order,
    _id: (order.order_id || "").toUpperCase(),
    _name: (order.shipping?.name || order.user_name || "cliente").split(" ")[0], // first name for warmth
    _address: order.shipping?.address || "",
    _pickup: [settings?.company_address, settings?.company_city].filter(Boolean).join(", "),
    _review_url: settings?.google_review_url || "",
  };
  const template = STATUS_TEMPLATE[status] || STATUS_TEMPLATE.pendiente;
  return template(enriched, brand);
}

/**
 * Open WhatsApp with a pre-armed message about the order status.
 * Returns { ok: true } on success, or { ok:false, reason } if phone missing.
 */
export function notifyWhatsApp(order, settings, statusOverride) {
  const phone = normalizePhone(order?.shipping?.phone);
  if (!phone) return { ok: false, reason: "El pedido no tiene WhatsApp del cliente." };
  const text = buildStatusMessage(order, settings, statusOverride);
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  // window.open with noopener to avoid opener-window leaks
  window.open(url, "_blank", "noopener,noreferrer");
  return { ok: true };
}
